import { Head, Link, router } from '@inertiajs/react'
import { PageProps } from '@/types'
import AppLayout from '@/layouts/AppLayout'
import { Fragment, useState, useEffect } from 'react'
import { RefreshCw, CheckCircle2, XCircle, AlertTriangle, Clock, KeyRound, UserX, X, SplitSquareHorizontal, Unlink, Link2, StopCircle } from 'lucide-react'

interface Pending {
  id: number
  status: string
  match_status: string
  comm_unique_key: string
  comm_type: string | null
  comm_commission_type: string | null
  comm_carrier: string | null
  comm_policy_number: string | null
  comm_insured_name: string | null
  comm_writing_agent_name: string | null
  comm_writing_agent_id: string | null
  comm_aggregate_amount: number
  override_agent_amount: number | null
  comm_payment_date: string | null
  payout_week_wednesday: string | null
  chargeback: boolean
  agent: { id: number; name: string } | null
  commission_id: number | null
  split: boolean
  split_commissions: { id: number; agent_id: number; agent_name: string; agent_amount: number }[]
  review_notes: string | null
  created_at: string
  auto_link_batch_id: string | null
  shortfall_breakdown: {
    carrier_payout_percentage: number
    received_amount: number
    expected_gross: number
    expected_agent_amount: number
    expected_sponsor_amount: number
    agent_shortfall: number
    sponsor_shortfall: number
    total_shortfall: number
    agent_name: string
    sponsor_name: string | null
  } | null
}

interface AgentOption {
  id: number
  name: string
  excluded_from_equity?: boolean
}

interface SchedulerInfo {
  enabled: boolean
  cron: string
  label: string
  next_run: string | null
}

interface RecordError {
  reason: string | null
  comm_unique_key: string | null
  comm_line_key: string | null
  writing_agent: string | null
  payout_week: string | null
}

interface Run {
  id: number
  status: string
  trigger_source: string | null
  payout_week_param: string | null
  payout_week_wednesday: string | null
  records_fetched: number
  records_new: number
  records_duplicate: number
  records_errored: number
  ffr_request_id: string | null
  error_message: string | null
  progress_label: string | null
  heartbeat_at: string | null
  record_error_summary: { reason: string; count: number }[]
  record_errors: RecordError[]
  started_at: string | null
  finished_at: string | null
}

interface AutoMatchedGroup {
  agent_id: number
  agent_name: string
  count: number
  batches: { batch_id: string; count: number }[]
}

interface UnmatchedWritingAgent {
  writing_agent_id: string
  name: string | null
  count: number
}

interface Props extends PageProps {
  records: Pending[]
  runs: Run[]
  agents: AgentOption[]
  counts: { needs_review: number; approved: number; rejected: number; all: number }
  auto_matched_groups: AutoMatchedGroup[]
  tab: string
  unmatched_writing_agents: UnmatchedWritingAgent[]
  writing_agent_filter: string | null
  ffr_configured: boolean
  scheduler_info: SchedulerInfo
}

const TABS: { key: string; label: string }[] = [
  { key: 'needs_review', label: 'Needs Review' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'all', label: 'All' },
]

const fmtMoney = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n)

export default function PendingCommissionsIndex({ records, runs, agents, counts, auto_matched_groups, tab, unmatched_writing_agents, writing_agent_filter, ffr_configured, scheduler_info }: Props) {
  const [pulling, setPulling] = useState(false)
  const [rejectingId, setRejectingId] = useState<number | null>(null)
  const [rejectReason, setRejectReason] = useState('')
  const [assigningId, setAssigningId] = useState<number | null>(null)
  const [splittingPending, setSplittingPending] = useState<Pending | null>(null)
  const [splitMode, setSplitMode] = useState<'percent' | 'dollar'>('percent')
  const [splitPrimary, setSplitPrimary] = useState<string>('50')
  const [splitSecondAgentId, setSplitSecondAgentId] = useState<string>('')
  const [splitSubmitting, setSplitSubmitting] = useState(false)

  const [expandedErrorRunId, setExpandedErrorRunId] = useState<number | null>(null)

  // Light polling: while any import run is still `running`, reload just the
  // `runs` prop every few seconds so the table reflects live progress. Stops
  // automatically once nothing is running. The controller also reaps stale
  // runs on each load, so a killed run flips to `failed` here too.
  const anyRunning = runs.some(r => r.status === 'running')
  useEffect(() => {
    if (!anyRunning) return
    const id = setInterval(() => {
      // `reload` always preserves scroll and state, so those options are implied.
      router.reload({ only: ['runs'] })
    }, 4000)
    return () => clearInterval(id)
  }, [anyRunning])

  const heartbeatAge = (iso: string | null): string | null => {
    if (!iso) return null
    const secs = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000))
    if (secs < 60) return `${secs}s ago`
    if (secs < 3600) return `${Math.floor(secs / 60)}m ago`
    return `${Math.floor(secs / 3600)}h ago`
  }

  const unlinkRow = (id: number) => {
    if (!confirm('Clear the auto-matched agent from this row? It will return to "manual match required" in the queue.')) return
    router.post(`/admin/pending_commissions/${id}/unlink`, {}, { preserveScroll: true })
  }

  const revertBatch = (batchId: string, count: number) => {
    if (!confirm(`Revert all ${count} auto-linked row${count === 1 ? '' : 's'} in this batch? They will all return to manual review.`)) return
    router.post('/admin/pending_commissions/revert_batch', { batch_id: batchId })
  }

  const assignAgent = (pendingId: number, agentId: string) => {
    if (!agentId) return
    openAssignDialog(pendingId, agentId)
  }

  const switchTab = (k: string) => router.get('/admin/pending_commissions', { tab: k }, { preserveState: false })

  const filterByWritingAgent = (id: string) => {
    router.get('/admin/pending_commissions', { tab: 'needs_review', writing_agent_id: id }, { preserveState: false })
  }

  const clearWritingAgentFilter = () => {
    router.get('/admin/pending_commissions', { tab }, { preserveState: false })
  }

  const [backfillWeeks, setBackfillWeeks] = useState<number>(8)
  const [specificWeek, setSpecificWeek] = useState<string>('')
  const [rangeStart, setRangeStart] = useState<string>('')
  const [rangeEnd, setRangeEnd] = useState<string>('')

  const pullWeek = (week: string) => {
    setPulling(true)
    router.post('/admin/pending_commissions/pull_now', { payout_week: week }, {
      onFinish: () => setPulling(false),
    })
  }

  const pullBackfill = () => {
    const n = Number(backfillWeeks)
    if (!Number.isInteger(n) || n < 1 || n > 52) {
      alert('Backfill weeks must be a whole number between 1 and 52.')
      return
    }
    setPulling(true)
    router.post('/admin/pending_commissions/pull_now', { payout_weeks: n }, {
      onFinish: () => setPulling(false),
    })
  }

  const pullSpecific = () => {
    if (!specificWeek) {
      alert('Pick a Wednesday date for the payout week.')
      return
    }
    pullWeek(specificWeek)
  }

  const pullRange = () => {
    if (!rangeStart || !rangeEnd) {
      alert('Pick a start and end date for the range.')
      return
    }
    if (rangeStart > rangeEnd) {
      alert('Start date must be on or before end date.')
      return
    }
    setPulling(true)
    router.post('/admin/pending_commissions/pull_now',
      { payout_week_start: rangeStart, payout_week_end: rangeEnd },
      { onFinish: () => setPulling(false) }
    )
  }

  const pullAll = () => {
    if (!confirm(
      "Import EVERY commission record FFR has for this account, then map them to agents in the review queue below.\n\n" +
      "FFR's API is slow for large pulls — this can take several minutes and runs in the background. " +
      "Existing rows are deduplicated by COMM_UNIQUE_KEY, so it's safe to re-run. Proceed?"
    )) return
    setPulling(true)
    router.post('/admin/pending_commissions/pull_now', { import_all: true }, {
      onFinish: () => setPulling(false),
    })
  }

  const approve = (id: number) => {
    if (!confirm('Promote this record to a real Commission? Equity credits will sync automatically.')) return
    router.post(`/admin/pending_commissions/${id}/approve`)
  }

  const openSplitModal = (p: Pending) => {
    setSplittingPending(p)
    setSplitMode('percent')
    setSplitPrimary('50')
    setSplitSecondAgentId('')
  }

  const closeSplitModal = () => {
    setSplittingPending(null)
    setSplitSubmitting(false)
  }

  const splitBaseAmount = splittingPending
    ? splittingPending.override_agent_amount ?? splittingPending.comm_aggregate_amount
    : 0

  const splitShares = (() => {
    if (!splittingPending) return { primary: 0, secondary: 0, valid: false }
    const primaryNum = Number(splitPrimary)
    if (!Number.isFinite(primaryNum) || primaryNum <= 0) return { primary: 0, secondary: 0, valid: false }
    if (splitMode === 'percent') {
      if (primaryNum >= 100) return { primary: 0, secondary: 0, valid: false }
      const p = Math.round(splitBaseAmount * primaryNum) / 100
      return { primary: p, secondary: splitBaseAmount - p, valid: true }
    }
    if (primaryNum >= splitBaseAmount) return { primary: 0, secondary: 0, valid: false }
    return { primary: primaryNum, secondary: splitBaseAmount - primaryNum, valid: true }
  })()

  const submitSplit = () => {
    if (!splittingPending) return
    if (!splitSecondAgentId) { alert('Pick a second agent.'); return }
    if (!splitShares.valid) { alert('Split values must sum to the original and be greater than zero.'); return }
    setSplitSubmitting(true)
    router.post(`/admin/pending_commissions/${splittingPending.id}/split_approve`, {
      second_agent_id: splitSecondAgentId,
      mode: splitMode,
      primary_value: splitMode === 'percent' ? Number(splitPrimary) : splitShares.primary,
      secondary_value: splitMode === 'percent' ? 100 - Number(splitPrimary) : splitShares.secondary,
    }, { onFinish: () => closeSplitModal() })
  }

  // Fanout preview dialog
  interface FanoutRow {
    id: number
    comm_writing_agent_id: string | null
    comm_writing_agent_name: string | null
    comm_carrier: string | null
    payout_week_wednesday: string | null
  }
  interface FanoutPreview {
    agent_name: string
    id_matches: { count: number; rows: FanoutRow[] }
    name_matches: { count: number; rows: FanoutRow[] }
    fetched_at: string
  }
  const [pendingAssign, setPendingAssign] = useState<{ pendingId: number; agentId: string; agentName: string } | null>(null)
  const [fanoutPreview, setFanoutPreview] = useState<FanoutPreview | null>(null)
  const [fanoutLoading, setFanoutLoading] = useState(false)
  const [fanoutError, setFanoutError] = useState(false)
  const [fanoutConfirming, setFanoutConfirming] = useState(false)

  const fetchFanoutPreview = (pendingId: number, agentId: string) => {
    setFanoutLoading(true)
    setFanoutError(false)
    setFanoutPreview(null)
    fetch(`/admin/pending_commissions/${pendingId}/fanout_preview?agent_id=${agentId}`, {
      headers: { Accept: 'application/json' }
    })
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        return r.json()
      })
      .then((data: FanoutPreview) => setFanoutPreview(data))
      .catch(() => setFanoutError(true))
      .finally(() => setFanoutLoading(false))
  }

  const openAssignDialog = (pendingId: number, agentId: string) => {
    if (!agentId) return
    const agent = agents.find(a => String(a.id) === String(agentId))
    setPendingAssign({ pendingId, agentId, agentName: agent?.name ?? '' })
    setAssigningId(null)
    fetchFanoutPreview(pendingId, agentId)
  }

  const [cancellingRunId, setCancellingRunId] = useState<number | null>(null)

  const cancelRun = (id: number) => {
    if (!confirm(
      'Stop this import run? Records already staged are kept — the run will be marked failed and you can re-run at any time.'
    )) return
    setCancellingRunId(id)
    router.post('/admin/pending_commissions/cancel_run', { run_id: id }, {
      preserveScroll: true,
      onFinish: () => setCancellingRunId(null),
    })
  }

  const cancelAssignDialog = () => {
    setPendingAssign(null)
    setFanoutPreview(null)
    setFanoutLoading(false)
    setFanoutError(false)
    setFanoutConfirming(false)
  }

  const confirmAssign = () => {
    if (!pendingAssign) return
    setFanoutConfirming(true)
    router.patch(
      `/admin/pending_commissions/${pendingAssign.pendingId}`,
      { agent_id: pendingAssign.agentId, tab },
      {
        preserveScroll: true,
        onFinish: () => {
          setPendingAssign(null)
          setFanoutPreview(null)
          setFanoutError(false)
          setFanoutConfirming(false)
        }
      }
    )
  }

  const [bulkApprovingAgentId, setBulkApprovingAgentId] = useState<number | null>(null)
  const bulkApprove = (group: AutoMatchedGroup) => {
    if (!confirm(
      `Approve all ${group.count} auto-matched pending commission${group.count === 1 ? '' : 's'} for ${group.agent_name}? ` +
      `Each will be promoted to a real Commission and equity credits will sync automatically.`
    )) return
    setBulkApprovingAgentId(group.agent_id)
    router.post('/admin/pending_commissions/bulk_approve',
      { agent_id: group.agent_id },
      { onFinish: () => setBulkApprovingAgentId(null) }
    )
  }

  const submitReject = () => {
    if (rejectingId == null) return
    router.post(`/admin/pending_commissions/${rejectingId}/reject`, { reason: rejectReason }, {
      onFinish: () => { setRejectingId(null); setRejectReason('') }
    })
  }

  return (
    <AppLayout>
      <Head title="Pending Commissions" />
      <div className="px-6 py-6 max-w-[1400px] mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-serif text-forest-green">Pending Commissions</h1>
            <p className="text-sm text-charcoal/70 mt-1">FFR commission imports awaiting review.</p>
          </div>
          <div className="flex gap-2">
            <Link
              href="/admin/ffr_credentials"
              className="px-4 py-2 bg-cream border border-charcoal/20 text-charcoal rounded hover:bg-charcoal/5 flex items-center gap-2"
            >
              <KeyRound className="w-4 h-4" />
              FFR Credentials
            </Link>
            <button
              onClick={() => pullWeek('current')}
              disabled={pulling || !ffr_configured}
              className="px-4 py-2 bg-cream border border-forest-green/30 text-forest-green rounded hover:bg-forest-green/10 disabled:opacity-50 flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${pulling ? 'animate-spin' : ''}`} />
              Pull Current Week
            </button>
            <button
              onClick={() => pullWeek('last')}
              disabled={pulling || !ffr_configured}
              className="px-4 py-2 bg-forest-green text-cream rounded hover:bg-forest-green/90 disabled:opacity-50 flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${pulling ? 'animate-spin' : ''}`} />
              Pull Last Week
            </button>
          </div>
        </div>

        {ffr_configured && (
          <div className="mb-4 p-3 border border-charcoal/15 bg-white rounded">
            <div className="flex flex-wrap items-end gap-4">
              <div className="flex items-end gap-2">
                <div>
                  <label className="block text-xs uppercase tracking-wide text-charcoal/60 mb-1">
                    Backfill recent weeks
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={52}
                    value={backfillWeeks}
                    onChange={e => setBackfillWeeks(Number(e.target.value))}
                    disabled={pulling}
                    className="border border-charcoal/20 rounded px-2 py-1.5 text-sm w-24"
                  />
                </div>
                <button
                  onClick={pullBackfill}
                  disabled={pulling}
                  className="px-3 py-1.5 bg-forest-green/10 border border-forest-green/30 text-forest-green rounded hover:bg-forest-green/20 disabled:opacity-50 flex items-center gap-2 text-sm"
                  title="Pulls the last N payout weeks. Existing records are skipped automatically."
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${pulling ? 'animate-spin' : ''}`} />
                  Run Backfill
                </button>
              </div>

              <div className="h-10 border-l border-charcoal/10" aria-hidden />

              <div className="flex items-end gap-2">
                <div>
                  <label className="block text-xs uppercase tracking-wide text-charcoal/60 mb-1">
                    Specific payout week (Wed.)
                  </label>
                  <input
                    type="date"
                    value={specificWeek}
                    onChange={e => setSpecificWeek(e.target.value)}
                    disabled={pulling}
                    className="border border-charcoal/20 rounded px-2 py-1.5 text-sm"
                  />
                </div>
                <button
                  onClick={pullSpecific}
                  disabled={pulling || !specificWeek}
                  className="px-3 py-1.5 bg-cream border border-charcoal/30 text-charcoal rounded hover:bg-charcoal/5 disabled:opacity-50 flex items-center gap-2 text-sm"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${pulling ? 'animate-spin' : ''}`} />
                  Pull This Week
                </button>
              </div>

              <div className="h-10 border-l border-charcoal/10" aria-hidden />

              <div className="flex items-end gap-2">
                <div>
                  <label className="block text-xs uppercase tracking-wide text-charcoal/60 mb-1">
                    Date range — start
                  </label>
                  <input
                    type="date"
                    value={rangeStart}
                    onChange={e => setRangeStart(e.target.value)}
                    disabled={pulling}
                    className="border border-charcoal/20 rounded px-2 py-1.5 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wide text-charcoal/60 mb-1">
                    end
                  </label>
                  <input
                    type="date"
                    value={rangeEnd}
                    onChange={e => setRangeEnd(e.target.value)}
                    disabled={pulling}
                    className="border border-charcoal/20 rounded px-2 py-1.5 text-sm"
                  />
                </div>
                <button
                  onClick={pullRange}
                  disabled={pulling || !rangeStart || !rangeEnd}
                  className="px-3 py-1.5 bg-cream border border-charcoal/30 text-charcoal rounded hover:bg-charcoal/5 disabled:opacity-50 flex items-center gap-2 text-sm"
                  title="Imports every Wednesday payout week that falls in the range. Range is snapped to whole payout weeks; max 52 weeks."
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${pulling ? 'animate-spin' : ''}`} />
                  Pull Range
                </button>
              </div>

              <div className="h-10 border-l border-charcoal/10" aria-hidden />

              <div className="flex items-end gap-2">
                <div>
                  <label className="block text-xs uppercase tracking-wide text-charcoal/60 mb-1">
                    Import everything
                  </label>
                  <button
                    onClick={pullAll}
                    disabled={pulling}
                    className="px-3 py-1.5 bg-gold/20 border border-gold text-charcoal rounded hover:bg-gold/30 disabled:opacity-50 flex items-center gap-2 text-sm font-medium"
                    title="Pull every commission record FFR has for this member, paginated. Then map them to agents below. Slow — runs in background."
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${pulling ? 'animate-spin' : ''}`} />
                    Import All Records
                  </button>
                </div>
              </div>

              <div className="ml-auto text-xs text-charcoal/60 max-w-xs">
                <strong>Safe to re-run:</strong> imports are deduplicated by FFR's
                <code className="mx-1">COMM_UNIQUE_KEY</code>. Any record already in the
                staging table is counted as a duplicate and skipped.
              </div>
            </div>
          </div>
        )}

        {scheduler_info && (
          <div className="mb-4 p-3 border border-forest-green/20 bg-forest-green/5 rounded text-sm text-charcoal flex items-center gap-2">
            <Clock className="w-4 h-4 text-forest-green flex-shrink-0" />
            <div>
              <strong>Scheduled pulls:</strong> {scheduler_info.label} (cron <code>{scheduler_info.cron}</code>).{' '}
              {scheduler_info.enabled
                ? scheduler_info.next_run
                  ? <>Next run: <span className="font-mono">{new Date(scheduler_info.next_run).toLocaleString()}</span></>
                  : 'Active.'
                : <span className="text-burgundy">Disabled in this environment.</span>}
            </div>
          </div>
        )}

        {!ffr_configured && (
          <div className="mb-4 p-4 border border-burgundy/30 bg-burgundy/5 rounded flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-burgundy flex-shrink-0 mt-0.5" />
            <div className="text-sm text-charcoal">
              <strong>FFR API credentials not configured.</strong> Open <Link href="/admin/ffr_credentials" className="underline text-forest-green">FFR Credentials</Link> to paste your client certificate, private key, and HMAC secret directly into the app — no env-var newline juggling required.
            </div>
          </div>
        )}

        {tab === 'needs_review' && auto_matched_groups.length > 0 && (
          <div className="mb-4 p-3 border border-forest-green/20 bg-forest-green/5 rounded">
            <div className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-forest-green flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="text-sm font-medium text-forest-green mb-2">
                  Auto-matched pending commissions
                </div>
                <div className="space-y-2">
                  {auto_matched_groups.map(g => (
                    <div key={g.agent_id} className="flex flex-wrap items-center gap-2">
                      <span className="text-xs text-charcoal/70 font-medium min-w-[120px]">{g.agent_name}</span>
                      <button
                        onClick={() => bulkApprove(g)}
                        disabled={bulkApprovingAgentId !== null}
                        className="px-3 py-1.5 text-xs bg-forest-green text-cream rounded hover:bg-forest-green/90 disabled:opacity-50 flex items-center gap-1"
                        title={`Promote all ${g.count} auto-matched rows for ${g.agent_name}`}
                      >
                        <CheckCircle2 className="w-3 h-3" />
                        Approve all {g.count}
                        {bulkApprovingAgentId === g.agent_id && <span className="ml-1 animate-pulse">…</span>}
                      </button>
                      {g.batches.map(b => (
                        <button
                          key={b.batch_id}
                          onClick={() => revertBatch(b.batch_id, b.count)}
                          className="px-3 py-1.5 text-xs border border-charcoal/25 text-charcoal/70 rounded hover:bg-burgundy/5 hover:border-burgundy/40 hover:text-burgundy flex items-center gap-1"
                          title={`Undo this fan-out batch — revert ${b.count} auto-linked row${b.count === 1 ? '' : 's'} for ${g.agent_name}`}
                        >
                          <Unlink className="w-3 h-3" />
                          Revert batch ({b.count})
                        </button>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {unmatched_writing_agents.length > 0 && (
          <div className="mb-4 border border-burgundy/20 bg-burgundy/5 rounded-lg overflow-hidden">
            <div className="px-4 py-2 flex items-center gap-2 border-b border-burgundy/15">
              <UserX className="w-4 h-4 text-burgundy" />
              <h2 className="text-sm font-medium text-burgundy">
                Unmapped FFR writing agents
                <span className="ml-2 text-xs font-normal text-charcoal/60">
                  {unmatched_writing_agents.length} {unmatched_writing_agents.length === 1 ? 'ID' : 'IDs'} with unassigned rows — map one to auto-link the rest.
                </span>
              </h2>
            </div>
            <ul className="divide-y divide-burgundy/10 max-h-56 overflow-y-auto">
              {unmatched_writing_agents.map(w => {
                const active = writing_agent_filter === w.writing_agent_id
                return (
                  <li key={w.writing_agent_id}>
                    <button
                      onClick={() => filterByWritingAgent(w.writing_agent_id)}
                      className={`w-full flex items-center justify-between px-4 py-2 text-left text-sm hover:bg-burgundy/10 ${active ? 'bg-burgundy/10' : ''}`}
                      title="Filter the queue to this writing agent's rows"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="font-mono text-xs px-1.5 py-0.5 bg-white border border-burgundy/20 rounded text-charcoal">
                          FFR {w.writing_agent_id}
                        </span>
                        <span className="truncate text-charcoal">{w.name || '— no name on file —'}</span>
                      </div>
                      <span className="text-xs px-2 py-0.5 bg-burgundy/15 text-burgundy rounded-full font-medium flex-shrink-0">
                        {w.count} unassigned
                      </span>
                    </button>
                  </li>
                )
              })}
            </ul>
          </div>
        )}

        {writing_agent_filter && (
          <div className="mb-3 flex items-center gap-2 text-sm">
            <span className="text-charcoal/60">Filtered to FFR writing agent:</span>
            <span className="px-2 py-0.5 bg-forest-green/10 text-forest-green rounded font-mono text-xs">
              {writing_agent_filter}
            </span>
            <button
              onClick={clearWritingAgentFilter}
              className="text-xs text-charcoal/60 hover:text-burgundy flex items-center gap-1"
            >
              <X className="w-3 h-3" /> Clear filter
            </button>
          </div>
        )}

        <div className="flex gap-1 border-b border-charcoal/10 mb-4">
          {TABS.map(t => (
            <button
              key={t.key}
              onClick={() => switchTab(t.key)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                tab === t.key
                  ? 'border-gold text-forest-green'
                  : 'border-transparent text-charcoal/60 hover:text-charcoal'
              }`}
            >
              {t.label}
              <span className="ml-2 text-xs px-2 py-0.5 bg-charcoal/10 rounded-full">
                {counts[t.key as keyof typeof counts] ?? 0}
              </span>
            </button>
          ))}
        </div>

        <div className="bg-white border border-charcoal/10 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-cream text-left text-xs uppercase tracking-wide text-charcoal/60">
              <tr>
                <th className="px-3 py-2">Payout Week</th>
                <th className="px-3 py-2">Carrier / Policy</th>
                <th className="px-3 py-2">Insured</th>
                <th className="px-3 py-2">Writing Agent</th>
                <th className="px-3 py-2">Matched</th>
                <th className="px-3 py-2 text-right">Amount</th>
                <th className="px-3 py-2">Type</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {records.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-3 py-8 text-center text-charcoal/50">
                    No records.
                  </td>
                </tr>
              )}
              {records.map(r => (
                <Fragment key={r.id}>
                <tr className="border-t border-charcoal/5 hover:bg-cream/40">
                  <td className="px-3 py-2 whitespace-nowrap">{r.payout_week_wednesday || '—'}</td>
                  <td className="px-3 py-2">
                    <div className="font-medium">{r.comm_carrier || '—'}</div>
                    <div className="text-xs text-charcoal/50">{r.comm_policy_number || '—'}</div>
                  </td>
                  <td className="px-3 py-2">{r.comm_insured_name || '—'}</td>
                  <td className="px-3 py-2">
                    <div>{r.comm_writing_agent_name || '—'}</div>
                    <div className="text-xs text-charcoal/50">FFR ID: {r.comm_writing_agent_id || '—'}</div>
                  </td>
                  <td className="px-3 py-2">
                    {assigningId === r.id ? (
                      <select
                        autoFocus
                        defaultValue=""
                        onBlur={() => setAssigningId(null)}
                        onChange={(e) => assignAgent(r.id, e.target.value)}
                        className="text-xs border border-charcoal/20 rounded px-1 py-0.5 max-w-[180px]"
                      >
                        <option value="" disabled>Select agent…</option>
                        {agents.map(a => (
                          <option key={a.id} value={a.id}>
                            {a.name}
                          </option>
                        ))}
                      </select>
                    ) : r.agent ? (
                      <button
                        onClick={() => setAssigningId(r.id)}
                        className="text-forest-green hover:underline text-left"
                        title="Click to reassign"
                      >
                        {r.agent.name}
                      </button>
                    ) : (
                      <button
                        onClick={() => setAssigningId(r.id)}
                        className="text-burgundy text-xs flex items-center gap-1 hover:underline"
                        title="Click to assign an agent"
                      >
                        <AlertTriangle className="w-3 h-3" /> Unmatched — assign
                      </button>
                    )}
                  </td>
                  <td className={`px-3 py-2 text-right font-mono ${r.chargeback ? 'text-burgundy' : ''}`}>
                    {fmtMoney(r.comm_aggregate_amount)}
                  </td>
                  <td className="px-3 py-2">
                    {r.chargeback ? (
                      <span className="px-2 py-0.5 text-xs rounded bg-burgundy/10 text-burgundy">Chargeback</span>
                    ) : (
                      <span className="px-2 py-0.5 text-xs rounded bg-forest-green/10 text-forest-green">
                        {r.comm_commission_type || 'Payout'}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {r.status === 'pending_review' || r.status === 'needs_attention' ? (
                      <div className="flex gap-1 justify-end flex-wrap">
                        <button
                          onClick={() => approve(r.id)}
                          disabled={!r.agent}
                          title={r.agent ? 'Approve and create Commission' : 'Match an agent first'}
                          className="px-2 py-1 text-xs bg-forest-green text-cream rounded hover:bg-forest-green/90 disabled:opacity-40 flex items-center gap-1"
                        >
                          <CheckCircle2 className="w-3 h-3" /> Approve
                        </button>
                        <button
                          onClick={() => openSplitModal(r)}
                          disabled={!r.agent}
                          title={r.agent ? 'Split between writing agent and a second agent' : 'Match an agent first'}
                          className="px-2 py-1 text-xs bg-gold/30 border border-gold text-charcoal rounded hover:bg-gold/40 disabled:opacity-40 flex items-center gap-1"
                        >
                          <SplitSquareHorizontal className="w-3 h-3" /> Split
                        </button>
                        {r.match_status === 'auto_matched' && (
                          <button
                            onClick={() => unlinkRow(r.id)}
                            title="Clear auto-matched agent — row returns to manual review"
                            className="px-2 py-1 text-xs border border-charcoal/25 text-charcoal/60 rounded hover:bg-burgundy/5 hover:border-burgundy/40 hover:text-burgundy flex items-center gap-1"
                          >
                            <Unlink className="w-3 h-3" /> Unlink
                          </button>
                        )}
                        <button
                          onClick={() => setRejectingId(r.id)}
                          className="px-2 py-1 text-xs border border-burgundy/30 text-burgundy rounded hover:bg-burgundy/5 flex items-center gap-1"
                        >
                          <XCircle className="w-3 h-3" /> Reject
                        </button>
                      </div>
                    ) : (
                      <div className="flex flex-col items-end gap-1">
                        <span className="text-xs text-charcoal/50 capitalize">{r.status.replace('_', ' ')}</span>
                        {r.split && (
                          <div className="text-xs text-charcoal/70 flex flex-col items-end">
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-gold/30 border border-gold text-charcoal">
                              <SplitSquareHorizontal className="w-3 h-3" /> Split
                            </span>
                            <div className="mt-0.5 space-x-1">
                              {r.split_commissions.map((c, i) => (
                                <Fragment key={c.id}>
                                  {i > 0 && <span className="text-charcoal/40">·</span>}
                                  <Link
                                    href={`/agents/${c.agent_id}/commissions/${c.id}`}
                                    className="text-forest-green hover:underline"
                                    title={`${c.agent_name}: ${fmtMoney(c.agent_amount)}`}
                                  >
                                    {c.agent_name.split(' ')[0]} {fmtMoney(c.agent_amount)}
                                  </Link>
                                </Fragment>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
                {r.shortfall_breakdown && (r.status === 'pending_review' || r.status === 'needs_attention') && (
                  <tr className="bg-gold/5 border-t border-gold/20">
                    <td colSpan={8} className="px-3 py-2">
                      <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-xs">
                        <span className="font-medium text-charcoal/80">
                          Carrier at {r.shortfall_breakdown.carrier_payout_percentage}% — shortfall breakdown:
                        </span>
                        <span><span className="text-charcoal/60">Received</span> <span className="font-mono">{fmtMoney(r.shortfall_breakdown.received_amount)}</span></span>
                        <span><span className="text-charcoal/60">Expected</span> <span className="font-mono">{fmtMoney(r.shortfall_breakdown.expected_gross)}</span></span>
                        <span><span className="text-charcoal/60">Shortfall</span> <span className="font-mono text-burgundy">{fmtMoney(r.shortfall_breakdown.total_shortfall)}</span></span>
                        <span className="text-charcoal/40">|</span>
                        <span><span className="text-charcoal/60">Agent ({r.shortfall_breakdown.agent_name})</span> expected <span className="font-mono">{fmtMoney(r.shortfall_breakdown.expected_agent_amount)}</span>, owed <span className="font-mono text-burgundy">{fmtMoney(r.shortfall_breakdown.agent_shortfall)}</span></span>
                        {r.shortfall_breakdown.sponsor_name && r.shortfall_breakdown.expected_sponsor_amount > 0 && (
                          <span><span className="text-charcoal/60">Sponsor ({r.shortfall_breakdown.sponsor_name})</span> expected <span className="font-mono">{fmtMoney(r.shortfall_breakdown.expected_sponsor_amount)}</span>, owed <span className="font-mono text-burgundy">{fmtMoney(r.shortfall_breakdown.sponsor_shortfall)}</span></span>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
                </Fragment>
              ))}
            </tbody>
          </table>
          </div>
        </div>

        <div className="mt-8">
          <h2 className="text-lg font-serif text-forest-green mb-2 flex items-center gap-2">
            <Clock className="w-4 h-4" /> Recent Import Runs
          </h2>
          <div className="bg-white border border-charcoal/10 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-cream text-left text-xs uppercase tracking-wide text-charcoal/60">
                <tr>
                  <th className="px-3 py-2">When</th>
                  <th className="px-3 py-2">Source</th>
                  <th className="px-3 py-2">Week</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Progress</th>
                  <th className="px-3 py-2 text-right">Fetched</th>
                  <th className="px-3 py-2 text-right">New</th>
                  <th className="px-3 py-2 text-right">Dupes</th>
                  <th className="px-3 py-2 text-right">Errors</th>
                  <th className="px-3 py-2">FFR Request ID</th>
                </tr>
              </thead>
              <tbody>
                {runs.length === 0 && (
                  <tr>
                    <td colSpan={10} className="px-3 py-6 text-center text-charcoal/50">No runs yet.</td>
                  </tr>
                )}
                {runs.map(r => {
                  const isRunning = r.status === 'running'
                  const hbAge = isRunning ? heartbeatAge(r.heartbeat_at) : null
                  const errSummary = r.record_error_summary || []
                  const showError =
                    ((r.status === 'failed' || r.status === 'partial') && !!r.error_message) ||
                    errSummary.length > 0
                  const expanded = expandedErrorRunId === r.id
                  return (
                    <Fragment key={r.id}>
                      <tr className="border-t border-charcoal/5">
                        <td className="px-3 py-2 whitespace-nowrap text-xs">{r.started_at ? new Date(r.started_at).toLocaleString() : '—'}</td>
                        <td className="px-3 py-2 text-xs">{r.trigger_source}</td>
                        <td className="px-3 py-2 text-xs">{r.payout_week_param || r.payout_week_wednesday || '—'}</td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-0.5 text-xs rounded inline-flex items-center gap-1 ${
                              r.status === 'success' ? 'bg-forest-green/10 text-forest-green'
                              : r.status === 'failed' ? 'bg-burgundy/10 text-burgundy'
                              : r.status === 'partial' ? 'bg-gold/20 text-charcoal'
                              : 'bg-forest-green/10 text-forest-green'
                            }`}>
                              {isRunning && <RefreshCw className="w-3 h-3 animate-spin" />}
                              {r.status}
                            </span>
                            {isRunning && (
                              <button
                                onClick={() => cancelRun(r.id)}
                                disabled={cancellingRunId === r.id}
                                className="px-2 py-0.5 text-xs border border-burgundy/40 text-burgundy rounded hover:bg-burgundy/10 disabled:opacity-50 flex items-center gap-1"
                                title="Stop this import run. Already-staged records are kept."
                              >
                                <StopCircle className="w-3 h-3" />
                                {cancellingRunId === r.id ? 'Cancelling…' : 'Cancel'}
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-xs text-charcoal/70 max-w-[260px]">
                          {isRunning ? (
                            <div>
                              <div className="truncate" title={r.progress_label || ''}>{r.progress_label || 'Working…'}</div>
                              {hbAge && (
                                <div className={`text-[11px] ${r.heartbeat_at && (Date.now() - new Date(r.heartbeat_at).getTime()) > 120000 ? 'text-burgundy' : 'text-charcoal/45'}`}>
                                  last update {hbAge}
                                </div>
                              )}
                            </div>
                          ) : '—'}
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-xs">{r.records_fetched}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs">{r.records_new}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs">{r.records_duplicate}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs">
                          {errSummary.length > 0 || r.records_errored > 0 ? (
                            <button
                              onClick={() => setExpandedErrorRunId(expanded ? null : r.id)}
                              className="text-burgundy hover:underline"
                              title="Show why records were skipped"
                            >
                              {r.records_errored}
                            </button>
                          ) : r.records_errored}
                        </td>
                        <td className="px-3 py-2 text-xs font-mono text-charcoal/60">{r.ffr_request_id || '—'}</td>
                      </tr>
                      {showError && (
                        <tr className="bg-burgundy/5">
                          <td colSpan={10} className="px-3 py-2 text-xs text-burgundy">
                            <div className="flex items-start gap-2">
                              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                              <div className="flex-1 space-y-1.5">
                                {r.error_message && (
                                  <div className="break-words whitespace-pre-wrap font-mono">{r.error_message}</div>
                                )}
                                {errSummary.length > 0 && (
                                  <div>
                                    <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5">
                                      <span className="font-medium">{r.records_errored} record{r.records_errored === 1 ? '' : 's'} skipped:</span>
                                      {errSummary.map((s, i) => (
                                        <span key={i} className="text-charcoal/80">{s.reason} <span className="font-mono">({s.count})</span></span>
                                      ))}
                                    </div>
                                    {r.record_errors.length > 0 && (
                                      <button
                                        onClick={() => setExpandedErrorRunId(expanded ? null : r.id)}
                                        className="mt-1 text-charcoal/60 hover:text-burgundy underline"
                                      >
                                        {expanded ? 'Hide details' : `Show ${r.record_errors.length} detail${r.record_errors.length === 1 ? '' : 's'}`}
                                      </button>
                                    )}
                                    {expanded && r.record_errors.length > 0 && (
                                      <div className="mt-2 max-h-64 overflow-y-auto border border-burgundy/20 rounded bg-white">
                                        <table className="w-full text-[11px]">
                                          <thead className="bg-burgundy/10 text-left text-charcoal/70">
                                            <tr>
                                              <th className="px-2 py-1">Reason</th>
                                              <th className="px-2 py-1">Unique key</th>
                                              <th className="px-2 py-1">Writing agent</th>
                                              <th className="px-2 py-1">Payout week</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {r.record_errors.map((e, i) => (
                                              <tr key={i} className="border-t border-burgundy/10 text-charcoal/80">
                                                <td className="px-2 py-1">{e.reason || '—'}</td>
                                                <td className="px-2 py-1 font-mono">{e.comm_unique_key || e.comm_line_key || '—'}</td>
                                                <td className="px-2 py-1">{e.writing_agent || '—'}</td>
                                                <td className="px-2 py-1 font-mono">{e.payout_week || '—'}</td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                        {r.records_errored > r.record_errors.length && (
                                          <div className="px-2 py-1 text-charcoal/50 italic">
                                            …and {r.records_errored - r.record_errors.length} more (only the first {r.record_errors.length} are stored).
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  )
                })}
              </tbody>
            </table>
            </div>
          </div>
        </div>

        {splittingPending && (
          <div className="fixed inset-0 bg-charcoal/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-xl border border-charcoal/15 p-6 max-w-2xl w-full mx-4">
              <h3 className="text-lg font-serif text-forest-green mb-1 flex items-center gap-2">
                <SplitSquareHorizontal className="w-5 h-5" /> Split & Approve
              </h3>
              <p className="text-sm text-charcoal/70 mb-4">
                Promote this pending row into <strong>two</strong> Commission records, both linked to the
                same pending entry so FFR reconciliation still counts it as one deal. Equity credits flow
                to each agent in proportion to their share.
              </p>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="border border-charcoal/15 rounded p-3 bg-cream/40">
                  <div className="text-xs uppercase tracking-wide text-charcoal/60 mb-1">Primary (writing) agent</div>
                  <div className="font-medium text-charcoal">{splittingPending.agent?.name || '—'}</div>
                  <div className="text-xs text-charcoal/60 mt-1">Locked to the row's matched agent.</div>
                </div>
                <div className="border border-charcoal/15 rounded p-3">
                  <div className="text-xs uppercase tracking-wide text-charcoal/60 mb-1">Second agent</div>
                  <select
                    value={splitSecondAgentId}
                    onChange={e => setSplitSecondAgentId(e.target.value)}
                    className="border border-charcoal/20 rounded px-2 py-1.5 text-sm w-full"
                  >
                    <option value="">Pick an agent…</option>
                    {agents
                      .filter(a => a.id !== splittingPending.agent?.id)
                      .map(a => (
                        <option key={a.id} value={a.id}>
                          {a.name}{a.excluded_from_equity ? ' (excluded from equity)' : ''}
                        </option>
                      ))}
                  </select>
                </div>
              </div>

              <div className="mb-3">
                <div className="text-xs uppercase tracking-wide text-charcoal/60 mb-2">Split mode</div>
                <div className="flex gap-2 mb-3">
                  <button
                    onClick={() => { setSplitMode('percent'); setSplitPrimary('50') }}
                    className={`px-3 py-1.5 text-sm rounded border ${splitMode === 'percent' ? 'bg-forest-green text-cream border-forest-green' : 'border-charcoal/20 text-charcoal'}`}
                  >
                    Percentage
                  </button>
                  <button
                    onClick={() => { setSplitMode('dollar'); setSplitPrimary((splitBaseAmount / 2).toFixed(2)) }}
                    className={`px-3 py-1.5 text-sm rounded border ${splitMode === 'dollar' ? 'bg-forest-green text-cream border-forest-green' : 'border-charcoal/20 text-charcoal'}`}
                  >
                    Dollar
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-charcoal/60 mb-1">
                      Primary agent share {splitMode === 'percent' ? '(%)' : '($)'}
                    </label>
                    <input
                      type="number"
                      min={0}
                      step={splitMode === 'percent' ? 1 : 0.01}
                      value={splitPrimary}
                      onChange={e => setSplitPrimary(e.target.value)}
                      className="border border-charcoal/20 rounded px-2 py-1.5 text-sm w-full"
                    />
                  </div>
                  <div className="text-sm">
                    <div className="text-xs text-charcoal/60 mb-1">Computed shares</div>
                    <div className="font-mono">
                      Primary: {fmtMoney(splitShares.primary)}
                    </div>
                    <div className="font-mono">
                      Second: {fmtMoney(splitShares.secondary)}
                    </div>
                    <div className="text-xs text-charcoal/50 mt-1">
                      Original agent amount: {fmtMoney(splitBaseAmount)}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex gap-2 justify-end mt-4">
                <button
                  onClick={closeSplitModal}
                  disabled={splitSubmitting}
                  className="px-4 py-2 text-sm border border-charcoal/20 rounded"
                >
                  Cancel
                </button>
                <button
                  onClick={submitSplit}
                  disabled={splitSubmitting || !splitShares.valid || !splitSecondAgentId}
                  className="px-4 py-2 text-sm bg-forest-green text-cream rounded hover:bg-forest-green/90 disabled:opacity-50 flex items-center gap-1"
                >
                  <CheckCircle2 className="w-4 h-4" /> Split & Approve
                </button>
              </div>
            </div>
          </div>
        )}

        {pendingAssign && (
          <div className="fixed inset-0 bg-charcoal/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-xl border border-charcoal/15 p-6 max-w-lg w-full mx-4">
              <h3 className="text-lg font-serif text-forest-green mb-1 flex items-center gap-2">
                <Link2 className="w-5 h-5" /> Assign Agent
              </h3>
              <p className="text-sm text-charcoal/70 mb-4">
                Assign <strong>{pendingAssign.agentName}</strong> to this commission?
              </p>

              {fanoutLoading && (
                <div className="flex items-center gap-2 text-sm text-charcoal/60 py-4">
                  <RefreshCw className="w-4 h-4 animate-spin" /> Calculating auto-links…
                </div>
              )}

              {!fanoutLoading && fanoutError && (
                <div className="border border-burgundy/30 bg-burgundy/5 rounded p-3 mb-4">
                  <div className="flex items-center gap-2 text-sm text-burgundy mb-2">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                    Could not load preview. Confirm is disabled until the preview loads successfully.
                  </div>
                  <button
                    onClick={() => pendingAssign && fetchFanoutPreview(pendingAssign.pendingId, pendingAssign.agentId)}
                    className="px-3 py-1.5 text-xs bg-white border border-burgundy/30 text-burgundy rounded hover:bg-burgundy/5"
                  >
                    Retry
                  </button>
                </div>
              )}

              {!fanoutLoading && fanoutPreview && (() => {
                const { id_matches, name_matches } = fanoutPreview
                const total = id_matches.count + name_matches.count
                return (
                  <div className="space-y-3 mb-5">
                    {total === 0 ? (
                      <p className="text-sm text-charcoal/60 italic">No other rows will be auto-linked.</p>
                    ) : (
                      <p className="text-sm text-charcoal/80">
                        This will also auto-link <strong>{total}</strong> other unassigned row{total === 1 ? '' : 's'}:
                      </p>
                    )}

                    {id_matches.count > 0 && (
                      <div>
                        <div className="flex items-center gap-2 text-xs font-medium text-forest-green mb-1">
                          <CheckCircle2 className="w-3.5 h-3.5" />
                          {id_matches.count} matched by writing-agent ID
                        </div>
                        <ul className="max-h-36 overflow-y-auto space-y-0.5 pl-5 border border-charcoal/10 rounded bg-cream/40 py-1">
                          {id_matches.rows.map(r => (
                            <li key={r.id} className="text-xs text-charcoal/70">
                              <span className="font-mono text-charcoal/50 mr-1">FFR {r.comm_writing_agent_id || '—'}</span>
                              {r.comm_carrier && <span className="mr-1">{r.comm_carrier} ·</span>}
                              {r.comm_writing_agent_name || '—'}
                              {r.payout_week_wednesday && <span className="text-charcoal/40 ml-1">({r.payout_week_wednesday})</span>}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {name_matches.count > 0 && (
                      <div className="border border-amber-300 bg-amber-50 rounded p-3">
                        <div className="flex items-center gap-2 text-xs font-medium text-amber-700 mb-1">
                          <AlertTriangle className="w-3.5 h-3.5" />
                          {name_matches.count} matched by name only — review carefully
                        </div>
                        <ul className="max-h-36 overflow-y-auto space-y-0.5 pl-2 mb-2">
                          {name_matches.rows.map(r => (
                            <li key={r.id} className="text-xs text-amber-800 flex items-center gap-2">
                              <span className="px-1 py-0.5 bg-amber-200 text-amber-700 rounded text-[10px] font-medium leading-none flex-shrink-0">NAME</span>
                              {r.comm_carrier && <span>{r.comm_carrier} ·</span>}
                              {r.comm_writing_agent_name || '—'}
                              {r.payout_week_wednesday && <span className="text-amber-600/60 ml-1">({r.payout_week_wednesday})</span>}
                            </li>
                          ))}
                        </ul>
                        <p className="text-xs text-amber-700">
                          Name matching is fuzzy — verify these rows belong to <strong>{pendingAssign.agentName}</strong> before confirming.
                        </p>
                      </div>
                    )}
                  </div>
                )
              })()}

              <div className="flex gap-2 justify-end mt-2">
                <button
                  onClick={cancelAssignDialog}
                  disabled={fanoutConfirming}
                  className="px-4 py-2 text-sm border border-charcoal/20 rounded hover:bg-charcoal/5 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmAssign}
                  disabled={fanoutLoading || fanoutError || !fanoutPreview || fanoutConfirming}
                  className="px-4 py-2 text-sm bg-forest-green text-cream rounded hover:bg-forest-green/90 disabled:opacity-50 flex items-center gap-1"
                >
                  {fanoutConfirming
                    ? <><RefreshCw className="w-4 h-4 animate-spin" /> Linking…</>
                    : <><CheckCircle2 className="w-4 h-4" /> Confirm &amp; Link</>
                  }
                </button>
              </div>
            </div>
          </div>
        )}

        {rejectingId != null && (
          <div className="fixed inset-0 bg-charcoal/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-xl border border-charcoal/15 p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-serif text-forest-green mb-2">Reject Pending Commission</h3>
              <p className="text-sm text-charcoal/70 mb-4">Optional reason (visible in the audit log):</p>
              <textarea
                value={rejectReason}
                onChange={e => setRejectReason(e.target.value)}
                rows={3}
                className="w-full border border-charcoal/20 rounded px-3 py-2 text-sm"
                placeholder="e.g. duplicate of policy #12345, wrong member"
              />
              <div className="flex gap-2 justify-end mt-4">
                <button
                  onClick={() => { setRejectingId(null); setRejectReason('') }}
                  className="px-4 py-2 text-sm border border-charcoal/20 rounded"
                >
                  Cancel
                </button>
                <button
                  onClick={submitReject}
                  className="px-4 py-2 text-sm bg-burgundy text-cream rounded hover:bg-burgundy/90"
                >
                  Reject
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  )
}
